import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #isPageScrollLocked = false;
  #previousBodyOverflow = '';
  #previousHtmlOverflow = '';
  #openTimestamp = 0; // track when drawer was last opened

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details?.querySelector('summary')?.addEventListener('click', this.#onSummaryClick, { capture: true });
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details?.querySelector('summary')?.removeEventListener('click', this.#onSummaryClick, { capture: true });
    this.#unlockPageScroll();
  }

  /**
   * Capture the hamburger summary click before the browser performs its native
   * details toggle. This keeps drawer visibility and scroll locking in one
   * explicit code path at every page scroll position.
   * @param {MouseEvent} event
   */
  #onSummaryClick = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    // This is the sole click handler for the hamburger summary.
    // The on:click="/toggle" attribute has been intentionally removed from the
    // summary element in header-drawer.liquid to prevent a double-toggle:
    // component.js document-level capture fires before this element-level capture,
    // so having both caused the drawer to open then immediately close on every tap.
    this.toggle(undefined, event);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   * The summary click is controlled explicitly so scroll-lock cannot engage without
   * the drawer also receiving its open state and visible menu-open class.
   * @param {string | Event} [target]
   * @param {Event} [event]
   */
  toggle(target, event) {
    const toggleEvent = target instanceof Event ? target : event;
    const toggleTarget = target instanceof Event ? undefined : target;
    const details = this.#getDetailsElement(toggleEvent);

    toggleEvent?.preventDefault?.();

    return details.hasAttribute('open') ? this.#close(details) : this.open(toggleTarget, toggleEvent);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;
    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '');
    }
    summary.setAttribute('aria-expanded', 'true');
    this.#lockPageScroll();
    this.preventInitialAccordionAnimations(details);
    this.#openTimestamp = Date.now(); // record when drawer opened
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close(event) {
    // Block close if it came from the backdrop within 200ms of opening.
    // This prevents the same tap that opened the drawer from immediately closing it
    // via the backdrop's on:click handler.
    const isBackdrop = (event?.target ?? event?.currentTarget)?.classList?.contains('menu-drawer__backdrop');
    const justOpened = Date.now() - this.#openTimestamp < 200;
    if (isBackdrop && justOpened) {
      return; // ignore — this is the same tap that opened the drawer
    }
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (details === this.refs.details) {
      this.#unlockPageScroll();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Lock the page behind the mobile drawer without using scroll position as a
   * condition. The drawer itself is fixed to the viewport and remains scrollable.
   */
  #lockPageScroll() {
    if (this.#isPageScrollLocked) return;

    this.#previousBodyOverflow = document.body.style.overflow;
    this.#previousHtmlOverflow = document.documentElement.style.overflow;
    this.#isPageScrollLocked = true;

    document.documentElement.setAttribute('scroll-lock', '');
    document.documentElement.setAttribute('data-fa-menu-scroll-lock', 'true');
    document.body.setAttribute('data-fa-menu-scroll-lock', 'true');
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');
  }

  /**
   * Restore page scrolling after the drawer closes or is disconnected.
   */
  #unlockPageScroll() {
    if (!this.#isPageScrollLocked && !document.documentElement.hasAttribute('data-fa-menu-scroll-lock')) return;

    this.#isPageScrollLocked = false;
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.removeAttribute('data-fa-menu-scroll-lock');
    document.body.removeAttribute('data-fa-menu-scroll-lock');
    document.body.style.overflow = this.#previousBodyOverflow;
    document.documentElement.style.overflow = this.#previousHtmlOverflow;
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');

  if (element.id === 'Details-menu-drawer-container') {
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.removeAttribute('data-fa-menu-scroll-lock');
    document.body.removeAttribute('data-fa-menu-scroll-lock');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }
}
